import { useCart, CartItem } from '../../../hooks/useCart';
import { useCurrency } from '../../../hooks/useCurrency';
import { useLanguage } from '../../../hooks/useLanguage';
import { useNavigate } from 'react-router-dom';
import { formatCOP, getDisplayPrice, getSecondaryPrice } from '../../../lib/currency';

interface RecommendedProduct {
  id: string;
  name: string;
  price: number;
  oldPrice?: number;
  discount: string;
  image: string;
  type: string;
  badge?: string;
}

const allProducts: RecommendedProduct[] = [
  {
    id: 'win10pro',
    name: 'Windows 10 PRO Original',
    price: 55385,
    oldPrice: 119900,
    discount: '-54%',
    image:
      'https://public.readdy.ai/ai/img_res/48b262117b6cfd27471d0f54512e895b.jpg',
    type: 'Licencia Digital - 1 PC',
    badge: 'Popular',
  },
  {
    id: 'win11pro',
    name: 'Windows 11 PRO Original',
    price: 65963,
    oldPrice: 129900,
    discount: '-49%',
    image:
      'https://readdy.ai/api/search-image?query=windows%2011%20pro%20software%20box%20product%20packaging%20modern%20blue%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=win2&orientation=squarish',
    type: 'Licencia Digital - 1 PC',
    badge: 'Nuevo',
  },
  {
    id: 'office2024pro',
    name: 'Office 2024 Professional Plus',
    price: 52530,
    oldPrice: 229900,
    discount: '-77%',
    image:
      'https://readdy.ai/api/search-image?query=Microsoft%20Office%202024%20Professional%20Plus%20digital%20license%20card%20flat%20design%20clean%20minimal%20white%20background%20orange%20accent%20color%20software%20product%20icon%20modern%20sleek%20packaging%20design%20high%20resolution%20studio%20product%20shot%20centered%20composition&width=400&height=400&seq=offpro2024v3&orientation=squarish',
    type: 'Licencia de por vida - 1 PC',
    badge: 'Best Seller',
  },
  {
    id: 'office2021pro',
    name: 'Office 2021 Professional Plus',
    price: 63650,
    oldPrice: 199900,
    discount: '-68%',
    image:
      'https://readdy.ai/api/search-image?query=Microsoft%20Office%202021%20Professional%20Plus%20digital%20license%20card%20flat%20design%20clean%20minimal%20white%20background%20dark%20blue%20accent%20color%20software%20product%20icon%20modern%20sleek%20packaging%20design%20high%20resolution%20studio%20product%20shot%20centered%20composition&width=400&height=400&seq=offpro2021v3&orientation=squarish',
    type: 'Licencia de por vida - 1 PC',
    badge: 'Best Price',
  },
  {
    id: 'kaspersky-standard-1',
    name: 'Kaspersky Standard 1 Dispositivo - 1 año',
    price: 100500,
    oldPrice: 195000,
    discount: '-48%',
    image:
      'https://readdy.ai/api/search-image?query=kaspersky%20standard%20antivirus%20software%20box%20product%20packaging%20green%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av2b&orientation=squarish',
    type: 'Licencia Digital - 1 año',
    badge: 'Security',
  },
  {
    id: 'mcafee-total-1',
    name: 'McAfee Total Protection 1 Dispositivo - 1 año',
    price: 85000,
    oldPrice: 180000,
    discount: '-53%',
    image:
      'https://readdy.ai/api/search-image?query=mcafee%20total%20protection%20antivirus%20software%20box%20product%20packaging%20red%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av5&orientation=squarish',
    type: 'Licencia Digital - 1 año',
    badge: 'Sale',
  },
  {
    id: 'eset-nod32-1',
    name: 'ESET NOD32 Antivirus 1 Dispositivo - 2 años',
    price: 80000,
    oldPrice: 230650,
    discount: '-65%',
    image:
      'https://readdy.ai/api/search-image?query=eset%20nod32%20antivirus%20software%20box%20product%20packaging%20orange%20teal%20theme%20clean%20white%20background%20studio%20lighting%20high%20quality%20product%20photography&width=250&height=250&seq=av6&orientation=squarish',
    type: 'Licencia Digital - 2 años',
    badge: 'Sale',
  },
  {
    id: 'adguard-vpn-1-año---5-dispositivos',
    name: 'AdGuard VPN 1 Año - 5 Dispositivos',
    price: 21950,
    oldPrice: 45000,
    discount: '-51%',
    image:
      'https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/6b8688c129017d7e175bddd371672b98.png',
    type: 'Licencia Digital - 1 año',
    badge: 'VPN',
  },
];

const formatPrice = (price: number) => formatCOP(price);

export default function RecommendedProducts() {
  const { items: cartItems, addItem } = useCart();
  const { currency } = useCurrency();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const totalItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const needsMore = 3 - totalItemCount;
  const showDiscountIncentive = needsMore > 0;

  const cartIds = new Set(cartItems.map((item) => item.id));
  const available = allProducts.filter((p) => !cartIds.has(p.id));
  const shuffled = [...available].sort(() => Math.random() - 0.5);
  const recommended = shuffled.slice(0, 4);

  if (recommended.length === 0) return null;

  const handleAdd = (product: RecommendedProduct) => {
    addItem({
      id: product.id,
      name: product.name,
      type: product.type,
      price: product.price,
      oldPrice: product.oldPrice,
      image: product.image,
    });
  };

  const badgeColors: Record<string, string> = {
    Popular: 'bg-[#0078D4]',
    Nuevo: 'bg-teal-500',
    'Best Seller': 'bg-[#D24726]',
    'Best Price': 'bg-emerald-600',
    Security: 'bg-green-600',
    Sale: 'bg-red-500',
    VPN: 'bg-emerald-600',
  };

  return (
    <section className="mt-8 sm:mt-12 mb-4">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div>
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <i className="ri-sparkling-2-line text-amber-500"></i>
            {t('recommended.title')}
          </h2>
          {showDiscountIncentive ? (
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              {t('recommended.add_more')}{' '}
              <strong className="text-emerald-600">
                {needsMore} {t('recommended.more_products')}
              </strong>{' '}
              {t('recommended.get_discount')}{' '}
              <strong className="text-emerald-600">{t('recommended.discount_label')}</strong>
            </p>
          ) : (
            <p className="text-xs sm:text-sm text-gray-600 mt-1">{t('recommended.complement')}</p>
          )}
        </div>
      </div>

      {showDiscountIncentive && (
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 flex items-center gap-3 sm:gap-4 shadow-lg">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ri-percent-line text-white text-xl sm:text-2xl"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-xs sm:text-sm">
              {t('recommended.volume_near')} {needsMore} {t('recommended.more_products')}{' '}
              {t('recommended.volume_near2')}
            </p>
            <p className="text-white/80 text-[10px] sm:text-xs mt-0.5">{t('recommended.volume_desc')}</p>
          </div>
          <div className="flex-shrink-0 hidden sm:block">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
              <span className="text-white font-bold text-lg">5%</span>
              <span className="text-white/80 text-xs block">OFF</span>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {recommended.map((product) => {
          const display = getDisplayPrice(product.price, product.oldPrice, currency);
          const secondary = getSecondaryPrice(product.price, product.oldPrice, currency);
          return (
            <div
              key={product.id}
              className="bg-white border border-gray-100 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 group flex flex-col"
            >
              <div className="relative">
                {product.badge && (
                  <span
                    className={`absolute top-2 left-2 sm:top-2.5 sm:left-2.5 ${
                      badgeColors[product.badge] || 'bg-red-500'
                    } text-white text-[9px] sm:text-[10px] font-semibold px-1.5 py-0.5 sm:px-2.5 rounded-full whitespace-nowrap z-10`}
                  >
                    {product.badge}
                  </span>
                )}
                <span className="absolute top-2 right-2 sm:top-2.5 sm:right-2.5 bg-gray-900 text-white text-[9px] sm:text-[10px] font-bold px-1.5 py-0.5 sm:px-2 rounded-full whitespace-nowrap z-10">
                  {product.discount}
                </span>
                <div className="w-full h-28 sm:h-36 bg-gray-50 flex items-center justify-center p-2 sm:p-3">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      // Fallback to a placeholder image if the provided URL fails
                      (e.currentTarget as HTMLImageElement).src =
                        'https://via.placeholder.com/250?text=Image+Not+Found';
                    }}
                  />
                </div>
              </div>
              <div className="p-3 sm:p-4 flex flex-col flex-1">
                <h3 className="text-[10px] sm:text-xs font-semibold text-gray-900 mb-0.5 sm:mb-1 line-clamp-2 min-h-[24px] sm:min-h-[32px]">
                  {product.name}
                </h3>
                <p className="text-[9px] sm:text-[10px] text-gray-500 mb-1.5 sm:mb-2">{product.type}</p>
                <div className="mt-auto">
                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-1.5 mb-0.5">
                    <span className="text-sm sm:text-base font-bold text-gray-900">{display.main}</span>
                    {display.oldMain && (
                      <span className="text-[9px] sm:text-[10px] text-gray-400 line-through">
                        {display.oldMain}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 sm:gap-1.5 mb-2 sm:mb-3">
                    <p className="text-[9px] sm:text-[10px] text-emerald-600 font-medium">
                      ≈ {secondary.current}
                    </p>
                  </div>
                  <button
                    onClick={() => handleAdd(product)}
                    className="w-full py-1.5 sm:py-2 rounded-lg font-medium text-[10px] sm:text-xs bg-gradient-to-r from-[#0078D4] to-[#00a2ed] text-white hover:from-[#006abc] hover:to-[#0090d4] transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1 sm:gap-1.5 shadow-sm hover:shadow-md"
                  >
                    <i className="ri-add-line"></i>
                    <span className="hidden sm:inline">{t('recommended.add_cart')}</span>
                    <span className="sm:hidden">{t('recommended.add_short')}</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
